import { CountryCodeEnum, LanguageEnum } from "./country.model";

// Base route path for all landing pages
export const LANDING_ROUTE_PATH = "/landing";
// Route param key used for landing country lookup
export const LANDING_ROUTE_COUNTRY_PARAM = "country";
// Route param key used for campaign landing lookup
export const LANDING_ROUTE_CAMPAIGN_PARAM = "campaign";
// Index of the :country segment in /landing/:country (0="/", 1="landing", 2=country)
export const LANDING_COUNTRY_SEGMENT_INDEX = 2;
// Index of the :campaign segment in /landing/:country/:campaign
export const LANDING_CAMPAIGN_SEGMENT_INDEX = 3;
// Fallback country when URL country is missing or invalid
export const LANDING_DEFAULT_COUNTRY = CountryCodeEnum.th;
// Public asset base path for landing images, vectors, and lottie files
export const LANDING_ASSET_BASE_PATH = "/assets/landing";
// UTM query params captured for landing attribution
export const LANDING_UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

// Publicly supported landing countries.
export const SUPPORTED_COUNTRIES: CountryCodeEnum[] = [
  CountryCodeEnum.th,
  CountryCodeEnum.ph,
  CountryCodeEnum.za,
];

/** Countries with landing content. The landing maps need an entry for each of these and for no other country. */
export type LandingCountry =
  | CountryCodeEnum.th
  | CountryCodeEnum.ph
  | CountryCodeEnum.id
  | CountryCodeEnum.za;

/** A landing map: one entry for each landing country, optional for other countries. */
export type LandingCountryMap<T> = Record<LandingCountry, T> &
  Partial<Record<CountryCodeEnum, T>>;

/** Languages that landing pages use. */
export type LandingLanguage =
  | LanguageEnum.en
  | LanguageEnum.th
  | LanguageEnum.ph
  | LanguageEnum.id
  | LanguageEnum.za;

// Maps country code to its primary language
export const LANDING_COUNTRY_LANGUAGE_MAP: LandingCountryMap<LanguageEnum> = {
  [CountryCodeEnum.th]: LanguageEnum.th,
  [CountryCodeEnum.ph]: LanguageEnum.ph,
  [CountryCodeEnum.id]: LanguageEnum.id,
  [CountryCodeEnum.za]: LanguageEnum.za,
};

// Country-specific Facebook page URLs
export const LANDING_FACEBOOK_LINK_MAP: LandingCountryMap<string> = {
  [CountryCodeEnum.th]: "https://www.facebook.com/finn.app.com.th",
  [CountryCodeEnum.ph]: "https://www.facebook.com/finn.app.com.ph",
  [CountryCodeEnum.id]: "https://www.facebook.com/finn.app.id",
  [CountryCodeEnum.za]: "https://www.facebook.com/finn.app.za",
};

// Supported floating action button visual variants.
export enum LandingFloatingButtonVariant {
  line = "line",
  facebook = "facebook",
  respond = "respond",
}

// Country-specific floating action button config.
export interface LandingFloatingButtonConfig {
  href: string;
  variant: LandingFloatingButtonVariant;
  iconPath: string;
  iconAlt: string;
  ariaLabel: string;
}

// Header top decorative vector shown behind hero content.
export const LANDING_HEADER_TOP_GRAPHIC_PATH = `${LANDING_ASSET_BASE_PATH}/vectors/graphic-header-left.svg`;

// Header mobile background vector used in the mobile media extension section.
export const LANDING_HEADER_MOBILE_BACKGROUND_PATH = `${LANDING_ASSET_BASE_PATH}/vectors/background-phone-img.svg`;
// Stable DOM id for the hero CTA used as the mobile-nav reveal threshold.
export const LANDING_HEADER_PRIMARY_CTA_ID = "landing-header-cta";

// Matches static finn-landing lottie-player speed="0.75".
export const LANDING_HEADER_LOTTIE_SPEED = 0.75;

// Philippines-specific desktop hero image.
export const LANDING_HEADER_BRAND_AMBASSADOR_PATH = `${LANDING_ASSET_BASE_PATH}/images/ba-ph-main.webp`;

// Philippines-specific mobile hero image.
export const LANDING_HEADER_MOBILE_PH_IMAGE_PATH = `${LANDING_ASSET_BASE_PATH}/images/finn-header-md.webp`;

export const LANDING_HEADER_ZA_PHONE_IMAGE_PATH = `${LANDING_ASSET_BASE_PATH}/images/za-hero-phone.png`;

// Country-specific hero animation paths.
export const LANDING_HEADER_ANIMATION_PATH_MAP: LandingCountryMap<string> = {
  [CountryCodeEnum.th]: `${LANDING_ASSET_BASE_PATH}/lottie/anim-5-th.json`,
  [CountryCodeEnum.ph]: `${LANDING_ASSET_BASE_PATH}/lottie/anim-4-ph.json`,
  [CountryCodeEnum.id]: `${LANDING_ASSET_BASE_PATH}/lottie/anim-4-id.json`,
  [CountryCodeEnum.za]: `${LANDING_ASSET_BASE_PATH}/lottie/anim-4-za.json`,
};

// Header media rendering type.
export enum LandingHeaderMediaType {
  lottie = "lottie",
  image = "image",
  none = "none",
}

// Header media config for desktop/mobile slots.
export interface LandingHeaderMediaConfig {
  type: LandingHeaderMediaType;
  path?: string;
  alt?: string;
}

// Resolved landing header config used by the header component.
export interface LandingHeaderConfig {
  topGraphicPath: string;
  mobileBackgroundPath: string;
  desktopMedia: LandingHeaderMediaConfig;
  mobileMedia: LandingHeaderMediaConfig;
}

// Optional per-country header media overrides on top of default lottie behavior.
export interface LandingHeaderCountryOverride {
  desktopMedia?: LandingHeaderMediaConfig;
  mobileMedia?: LandingHeaderMediaConfig;
}

// Country-specific header overrides.
export const LANDING_HEADER_COUNTRY_OVERRIDE_MAP: Partial<
  Record<CountryCodeEnum, LandingHeaderCountryOverride>
> = {
  [CountryCodeEnum.ph]: {
    desktopMedia: {
      type: LandingHeaderMediaType.image,
      path: LANDING_HEADER_BRAND_AMBASSADOR_PATH,
      alt: "Brand Ambassador",
    },
    mobileMedia: {
      type: LandingHeaderMediaType.image,
      path: LANDING_HEADER_MOBILE_PH_IMAGE_PATH,
      alt: "FINN mobile header image",
    },
  },
  [CountryCodeEnum.za]: {
    desktopMedia: {
      type: LandingHeaderMediaType.image,
      path: LANDING_HEADER_ZA_PHONE_IMAGE_PATH,
      alt: "FINN app showing an available advance of R2,000",
    },
    mobileMedia: {
      type: LandingHeaderMediaType.image,
      path: LANDING_HEADER_ZA_PHONE_IMAGE_PATH,
      alt: "FINN app showing an available advance of R2,000",
    },
  },
};

// Country-to-FAB floating button config mapping.
export const LANDING_FLOATING_BUTTON_UI_CONFIG_MAP: LandingCountryMap<LandingFloatingButtonConfig> =
  {
    [CountryCodeEnum.th]: {
      href: "https://page.line.me/finn.app.th",
      variant: LandingFloatingButtonVariant.line,
      iconPath: `${LANDING_ASSET_BASE_PATH}/images/line-icon.png`,
      iconAlt: "Line chat icon",
      ariaLabel: "Chat with FINN on Line",
    },
    [CountryCodeEnum.ph]: {
      href: "https://m.me/finn.app.com.ph",
      variant: LandingFloatingButtonVariant.facebook,
      iconPath: `${LANDING_ASSET_BASE_PATH}/images/fb-icon.png`,
      iconAlt: "Facebook Messenger icon",
      ariaLabel: "Chat with FINN on Facebook Messenger",
    },
    [CountryCodeEnum.id]: {
      href: "https://m.me/finn.app.id",
      variant: LandingFloatingButtonVariant.facebook,
      iconPath: `${LANDING_ASSET_BASE_PATH}/images/fb-icon.png`,
      iconAlt: "Facebook Messenger icon",
      ariaLabel: "Chat with FINN on Facebook Messenger",
    },
    [CountryCodeEnum.za]: {
      href: "https://m.me/finn.app.za",
      variant: LandingFloatingButtonVariant.facebook,
      iconPath: `${LANDING_ASSET_BASE_PATH}/images/fb-icon.png`,
      iconAlt: "Facebook Messenger icon",
      ariaLabel: "Chat with FINN on Facebook Messenger",
    },
  };

// Footer brand logo displayed in the company section.
export const LANDING_FOOTER_LOGO_PATH = `${LANDING_ASSET_BASE_PATH}/vectors/finn.svg`;

// Footer partner logo used in Indonesia bottom bar.
export const LANDING_FOOTER_PARTNER_LOGO_PATH = `${LANDING_ASSET_BASE_PATH}/vectors/finn-powered-by.svg`;

// Footer social profile link and icon config.
export interface LandingFooterSocialLink {
  href: string;
  iconPath: string;
  iconAlt: string;
}

// Footer per-country feature toggles.
export interface LandingFooterCountryConfig {
  showFaqLink: boolean;
  showSocialLinks: boolean;
  showSecurityBadge: boolean;
  showAddress: boolean;
  showBottomBar: boolean;
  showPartnerLogo: boolean;
}

// Footer social links shown per country.
export const LANDING_FOOTER_SOCIAL_LINKS_MAP: Partial<
  Record<CountryCodeEnum, LandingFooterSocialLink[]>
> = {
  [CountryCodeEnum.th]: [
    {
      href: "https://www.facebook.com/finn.app.th",
      iconPath: `${LANDING_ASSET_BASE_PATH}/images/social-icons/facebook.png`,
      iconAlt: "Facebook",
    },
    {
      href: "https://page.line.me/finn.app.th",
      iconPath: `${LANDING_ASSET_BASE_PATH}/images/social-icons/line.png`,
      iconAlt: "LINE",
    },
    {
      href: "https://www.tiktok.com/@finn.app.th/",
      iconPath: `${LANDING_ASSET_BASE_PATH}/images/social-icons/tiktok.png`,
      iconAlt: "TikTok",
    },
    {
      href: "https://www.youtube.com/@FINN-APP",
      iconPath: `${LANDING_ASSET_BASE_PATH}/images/social-icons/youtube.png`,
      iconAlt: "YouTube",
    },
  ],
};

// Footer chat channel links shown in the contact section per country.
export const LANDING_FOOTER_CONTACT_CHAT_LINK_MAP: Partial<
  Record<CountryCodeEnum, string>
> = {
  [CountryCodeEnum.ph]: "https://m.me/finn.app.com.ph",
  [CountryCodeEnum.id]: "https://m.me/finn.app.id",
};

// Footer behavior differences per country.
export const LANDING_FOOTER_COUNTRY_CONFIG_MAP: LandingCountryMap<LandingFooterCountryConfig> =
  {
    [CountryCodeEnum.th]: {
      showFaqLink: true,
      showSocialLinks: true,
      showSecurityBadge: false,
      showAddress: true,
      showBottomBar: true,
      showPartnerLogo: false,
    },
    [CountryCodeEnum.ph]: {
      showFaqLink: true,
      showSocialLinks: false,
      showSecurityBadge: true,
      showAddress: true,
      showBottomBar: true,
      showPartnerLogo: false,
    },
    [CountryCodeEnum.id]: {
      showFaqLink: false,
      showSocialLinks: false,
      showSecurityBadge: false,
      showAddress: true,
      showBottomBar: true,
      showPartnerLogo: true,
    },
    [CountryCodeEnum.za]: {
      showFaqLink: false,
      showSocialLinks: false,
      showSecurityBadge: false,
      showAddress: false,
      showBottomBar: false,
      showPartnerLogo: false,
    },
  };

// Country-specific calendar SVG paths for landing hero sections
export const LANDING_CALENDAR_IMAGE_MAP: LandingCountryMap<string> = {
  [CountryCodeEnum.th]: `${LANDING_ASSET_BASE_PATH}/vectors/calendar-img.svg`,
  [CountryCodeEnum.ph]: `${LANDING_ASSET_BASE_PATH}/vectors/calendar-img-ph.svg`,
  [CountryCodeEnum.id]: `${LANDING_ASSET_BASE_PATH}/vectors/calendar-img-id.svg`,
  [CountryCodeEnum.za]: `${LANDING_ASSET_BASE_PATH}/vectors/calendar-img-za.svg`,
};

// ============================================================================
// LOW INTEREST SECTION
// ============================================================================

// Country-specific Finn mascot images for low-interest section
export const LANDING_LOW_INTEREST_IMAGE_MAP: LandingCountryMap<string> = {
  [CountryCodeEnum.th]: `${LANDING_ASSET_BASE_PATH}/images/finn-no-interest-th.webp`,
  [CountryCodeEnum.ph]: `${LANDING_ASSET_BASE_PATH}/images/finn-no-interest-ph.webp`,
  [CountryCodeEnum.id]: `${LANDING_ASSET_BASE_PATH}/images/finn-no-interest-id.webp`,
  [CountryCodeEnum.za]: `${LANDING_ASSET_BASE_PATH}/images/finn-simple-fast-za.svg`,
};

// Country-specific currency icons for low-interest section
export const LANDING_LOW_INTEREST_ICON_MAP: LandingCountryMap<string> = {
  [CountryCodeEnum.th]: `${LANDING_ASSET_BASE_PATH}/images/icon-money-th.png`,
  [CountryCodeEnum.ph]: `${LANDING_ASSET_BASE_PATH}/images/icon-money-ph.png`,
  [CountryCodeEnum.id]: `${LANDING_ASSET_BASE_PATH}/images/icon-money-id.png`,
  [CountryCodeEnum.za]: `${LANDING_ASSET_BASE_PATH}/images/icon-money-za.png`,
};

// ============================================================================
// LEVEL UP CTA SECTION
// ============================================================================

// Level-up section decorative graphic
export const LANDING_LEVEL_UP_GRAPHIC_PATH = `${LANDING_ASSET_BASE_PATH}/vectors/graphic-level-up.svg`;

// ============================================================================
// SAFE CONNECT SECTION
// ============================================================================

// Safe connect section icon
export const LANDING_SAFE_CONNECT_ICON_PATH = `${LANDING_ASSET_BASE_PATH}/images/icon-safe-connect.png`;

// Country-specific secure mascot images for safe-connect section
export const LANDING_SAFE_CONNECT_IMAGE_MAP: LandingCountryMap<string> = {
  [CountryCodeEnum.th]: `${LANDING_ASSET_BASE_PATH}/images/finn-secure.png`,
  [CountryCodeEnum.ph]: `${LANDING_ASSET_BASE_PATH}/images/finn-secure-ph.webp`,
  [CountryCodeEnum.id]: `${LANDING_ASSET_BASE_PATH}/images/finn-secure.png`,
  [CountryCodeEnum.za]: `${LANDING_ASSET_BASE_PATH}/images/finn-secure.png`,
};

// ============================================================================
// MILESTONES SECTION
// ============================================================================

// Milestone underline marker asset.
export const LANDING_MILESTONES_UNDERLINE_PATH = `${LANDING_ASSET_BASE_PATH}/vectors/underline.svg`;

// Milestone icon assets.
export const LANDING_MILESTONE_IMPROVE_ICON_PATH = `${LANDING_ASSET_BASE_PATH}/vectors/milestone-improve.svg`;
export const LANDING_MILESTONE_HIGHER_ICON_PATH = `${LANDING_ASSET_BASE_PATH}/vectors/milestone-higher.svg`;
export const LANDING_MILESTONE_UNLOCK_ICON_PATH = `${LANDING_ASSET_BASE_PATH}/vectors/milestone-unlock.svg`;

// Milestone item config used by the milestones section.
export interface LandingMilestoneItem {
  id: "improve" | "higher" | "unlock";
  iconPath: string;
  titleKeyBase: string;
  subtitleKey: string;
  accent: "accent" | "primary" | "cta";
}

// Shared milestone items (translation driven).
export const LANDING_MILESTONE_ITEMS: LandingMilestoneItem[] = [
  {
    id: "improve",
    iconPath: LANDING_MILESTONE_IMPROVE_ICON_PATH,
    titleKeyBase: "LANDING.MISSION_STATEMENT.GOAL_1.TITLE",
    subtitleKey: "LANDING.MISSION_STATEMENT.GOAL_1.SUB_TITLE",
    accent: "accent",
  },
  {
    id: "higher",
    iconPath: LANDING_MILESTONE_HIGHER_ICON_PATH,
    titleKeyBase: "LANDING.MISSION_STATEMENT.GOAL_2.TITLE",
    subtitleKey: "LANDING.MISSION_STATEMENT.GOAL_2.SUB_TITLE",
    accent: "primary",
  },
  {
    id: "unlock",
    iconPath: LANDING_MILESTONE_UNLOCK_ICON_PATH,
    titleKeyBase: "LANDING.MISSION_STATEMENT.GOAL_3.TITLE",
    subtitleKey: "LANDING.MISSION_STATEMENT.GOAL_3.SUB_TITLE",
    accent: "cta",
  },
];

// Each landing country uses the same milestone list.
export const LANDING_MILESTONE_ITEMS_MAP: LandingCountryMap<
  LandingMilestoneItem[]
> = {
  [CountryCodeEnum.th]: LANDING_MILESTONE_ITEMS,
  [CountryCodeEnum.ph]: LANDING_MILESTONE_ITEMS,
  [CountryCodeEnum.id]: LANDING_MILESTONE_ITEMS,
  [CountryCodeEnum.za]: LANDING_MILESTONE_ITEMS,
};

// ============================================================================
// FLEXIBILITY CTA SECTION
// ============================================================================

// Flexibility section edge graphic.
export const LANDING_FLEXIBILITY_EDGE_PATH = `${LANDING_ASSET_BASE_PATH}/vectors/cta-banner-edge.svg`;

// Config shape for legal/corporate footer links
export interface LandingCorporateLinkConfig {
  domain: string;
  localeParam?: string;
  countryParam?: string;
}

// Landing legal page slugs used when building country-aware corporate links.
export const LANDING_CORPORATE_LINK_FAQ = "faq";
export const LANDING_CORPORATE_LINK_PRIVACY_POLICY = "privacy-policy";
export const LANDING_CORPORATE_LINK_TERMS_AND_CONDITIONS =
  "terms-and-conditions";

// Country-specific legal page domains and query params
export const LANDING_CORPORATE_LINK_CONFIG: LandingCountryMap<LandingCorporateLinkConfig> =
  {
    [CountryCodeEnum.id]: {
      domain: "https://finn-indonesia-legal.surge.sh",
    },
    [CountryCodeEnum.za]: {
      domain: "https://finn-za-legal.surge.sh",
    },
    [CountryCodeEnum.th]: {
      domain: "https://www.finn-app.com",
      localeParam: "th-th",
    },
    [CountryCodeEnum.ph]: {
      domain: "https://www.finn-app.com",
      countryParam: "ph",
      localeParam: "ph-en",
    },
  };

// Maps app language enum to BCP 47 HTML lang attribute values
export const LANDING_HTML_LANG_MAP: Record<LandingLanguage, string> &
  Partial<Record<LanguageEnum, string>> = {
  [LanguageEnum.en]: "en",
  [LanguageEnum.th]: "th",
  [LanguageEnum.ph]: "en-PH",
  [LanguageEnum.id]: "id",
  [LanguageEnum.za]: "en-ZA",
};

// SEO: robots meta directive (prevents indexing during migration)
export const LANDING_SEO_ROBOTS_DIRECTIVE = "noindex, nofollow";
// SEO: attribute key used to mark landing-managed meta tags
export const LANDING_SEO_MANAGED_ATTRIBUTE = "data-landing-seo";
// SEO: attribute value used to mark landing-managed meta tags
export const LANDING_SEO_MANAGED_ATTRIBUTE_VALUE = "true";

// SEO: Twitter card type for social sharing
export const LANDING_SEO_TWITTER_CARD_TYPE = "summary_large_image";

// SEO: Open Graph content type
export const LANDING_SEO_OG_TYPE = "website";

// SEO: Open Graph site name
export const LANDING_SEO_OG_SITE_NAME = "FINN App";

// SEO: Default OG image path shared across all countries
export const LANDING_SEO_OG_IMAGE_PATH = `${LANDING_ASSET_BASE_PATH}/images/finn-icon.png`;

// i18n keys for landing SEO meta tags
export const LANDING_SEO_TRANSLATION_KEYS = {
  TITLE: "LANDING.SEO.TITLE",
  DESCRIPTION: "LANDING.SEO.DESCRIPTION",
  KEYWORDS: "LANDING.SEO.KEYWORDS",
  OFFER_DESCRIPTION: "LANDING.SEO.OFFER_DESCRIPTION",
} as const;

// ISO 4217 currency codes by country (used in structured data)
export const LANDING_COUNTRY_CURRENCY_MAP: LandingCountryMap<string> = {
  [CountryCodeEnum.th]: "THB",
  [CountryCodeEnum.ph]: "PHP",
  [CountryCodeEnum.id]: "IDR",
  [CountryCodeEnum.za]: "ZAR",
};

// Open Graph locale codes by country
export const LANDING_COUNTRY_LOCALE_MAP: LandingCountryMap<string> = {
  [CountryCodeEnum.th]: "th_TH",
  [CountryCodeEnum.ph]: "en_PH",
  [CountryCodeEnum.id]: "id_ID",
  [CountryCodeEnum.za]: "en_ZA",
};

// FINN social profile URLs for JSON-LD sameAs field
export const LANDING_SOCIAL_PROFILE_LINKS = {
  twitter: "https://twitter.com/finnapp",
  linkedin: "https://www.linkedin.com/company/finnapp",
} as const;

// Schema.org FinancialService structure for JSON-LD injection
export interface LandingStructuredData {
  "@context": "https://schema.org";
  "@type": "FinancialService";
  name: string;
  description: string;
  url: string;
  logo: string;
  sameAs: string[];
  offers: {
    "@type": "Offer";
    description: string;
    priceCurrency: string;
  };
}

// Resolved default landing route context from URL parsing
export interface DefaultLandingRouteContext {
  kind?: "default";
  country: CountryCodeEnum;
  isCanonicalPath: boolean;
  canonicalPath: string;
}

// Resolved campaign landing route context from URL parsing
export interface CampaignLandingRouteContext {
  kind: "campaign";
  country: CountryCodeEnum;
  campaignSlug: string;
  isCanonicalPath: boolean;
  canonicalPath: string;
}

// Entry-aware landing route context used by bootstrap, guards, and pages.
export type LandingRouteContext =
  | DefaultLandingRouteContext
  | CampaignLandingRouteContext;

// ============================================================================
// SELLING POINTS SECTION (PH/ID/ZA only)
// ============================================================================

// Selling point identifiers.
export enum LandingSellingPointId {
  interest = "interest",
  fees = "fees",
  withdrawal = "withdrawal",
}

// Selling point item config used by the selling-points section.
export interface LandingSellingPointItem {
  id: LandingSellingPointId;
  headingKey: string;
  titleKey: string;
  descriptionKey: string;
}

// Countries that display the selling points section.
export const LANDING_SELLING_POINTS_COUNTRIES: CountryCodeEnum[] = [
  CountryCodeEnum.ph,
  CountryCodeEnum.id,
  CountryCodeEnum.za,
];

// Selling points items (translation driven, shared across supported countries).
export const LANDING_SELLING_POINT_ITEMS: LandingSellingPointItem[] = [
  {
    id: LandingSellingPointId.interest,
    headingKey: "LANDING.SELLING_POINTS.ITEM_1.HEADING",
    titleKey: "LANDING.SELLING_POINTS.ITEM_1.TITLE",
    descriptionKey: "LANDING.SELLING_POINTS.ITEM_1.DESCRIPTION",
  },
  {
    id: LandingSellingPointId.fees,
    headingKey: "LANDING.SELLING_POINTS.ITEM_2.HEADING",
    titleKey: "LANDING.SELLING_POINTS.ITEM_2.TITLE",
    descriptionKey: "LANDING.SELLING_POINTS.ITEM_2.DESCRIPTION",
  },
  {
    id: LandingSellingPointId.withdrawal,
    headingKey: "LANDING.SELLING_POINTS.ITEM_3.HEADING",
    titleKey: "LANDING.SELLING_POINTS.ITEM_3.TITLE",
    descriptionKey: "LANDING.SELLING_POINTS.ITEM_3.DESCRIPTION",
  },
];

// Default CTA translation key used by the selling-points section.
export const LANDING_SELLING_POINTS_DEFAULT_CTA_KEY = "LANDING.SIGN_UP";

// Country-specific CTA overrides for selling-points.
export const LANDING_SELLING_POINTS_CTA_KEY_MAP: Partial<
  Record<CountryCodeEnum, string>
> = {
  [CountryCodeEnum.za]: "LANDING.SELLING_POINTS.CTA",
};

// ============================================================================
// SUPPORTED BANKS SECTION (TH only)
// ============================================================================

// Supported bank identifiers.
export enum LandingSupportedBankId {
  scb = "scb",
  kasikorn = "kasikorn",
  bangkok = "bangkok",
  ayudhya = "ayudhya",
  ktb = "ktb",
}

// Bank item config used by the supported-banks section.
export interface LandingSupportedBankItem {
  id: LandingSupportedBankId;
  name: string;
  nameKey: string;
  logoPath: string;
}

// Countries that display the supported banks section.
export const LANDING_SUPPORTED_BANKS_COUNTRIES: CountryCodeEnum[] = [
  CountryCodeEnum.th,
];

// Bank logo base path.
export const LANDING_BANKS_LOGO_BASE_PATH = `${LANDING_ASSET_BASE_PATH}/vectors/banks`;

// Supported bank items for Thailand.
export const LANDING_SUPPORTED_BANK_ITEMS: LandingSupportedBankItem[] = [
  {
    id: LandingSupportedBankId.scb,
    name: "SCB",
    nameKey: "LANDING.SUPPORTED_BANKS.SCB",
    logoPath: `${LANDING_BANKS_LOGO_BASE_PATH}/scb.svg`,
  },
  {
    id: LandingSupportedBankId.kasikorn,
    name: "Kasikornbank",
    nameKey: "LANDING.SUPPORTED_BANKS.KASIKORN",
    logoPath: `${LANDING_BANKS_LOGO_BASE_PATH}/farmers-bank.svg`,
  },
  {
    id: LandingSupportedBankId.bangkok,
    name: "Bangkok Bank",
    nameKey: "LANDING.SUPPORTED_BANKS.BANGKOK",
    logoPath: `${LANDING_BANKS_LOGO_BASE_PATH}/bangkok.svg`,
  },
  {
    id: LandingSupportedBankId.ayudhya,
    name: "Bank of Ayudhya",
    nameKey: "LANDING.SUPPORTED_BANKS.AYUDHYA",
    logoPath: `${LANDING_BANKS_LOGO_BASE_PATH}/ayudhya.svg`,
  },
  {
    id: LandingSupportedBankId.ktb,
    name: "Krungthai Bank",
    nameKey: "LANDING.SUPPORTED_BANKS.KTB",
    logoPath: `${LANDING_BANKS_LOGO_BASE_PATH}/ktb.svg`,
  },
];

// ============================================================================
// TESTIMONIALS SECTION
// ============================================================================

// VocalVideo gallery link for Thailand testimonials CTA.
export const LANDING_TESTIMONIALS_VOCALVIDEO_GALLERY_URL =
  "https://vocalvideo.com/galleries/finn-app-pte-ltd-video-gallery";

// Quote icon asset for Thailand user testimonials.
export const LANDING_TESTIMONIALS_QUOTE_ICON_PATH = `${LANDING_ASSET_BASE_PATH}/vectors/quote-icon.svg`;

// Reviewer image assets for standard testimonials (PH/ID/ZA).
export const LANDING_TESTIMONIALS_REVIEWER_IMAGES = {
  reviewer1: `${LANDING_ASSET_BASE_PATH}/images/reviewers/reviewer_1.webp`,
  reviewer2: `${LANDING_ASSET_BASE_PATH}/images/reviewers/reviewer_2.webp`,
  reviewer3: `${LANDING_ASSET_BASE_PATH}/images/reviewers/reviewer_3.webp`,
  reviewer4: `${LANDING_ASSET_BASE_PATH}/images/reviewers/reviewer_4.webp`,
} as const;

// Thailand user avatar assets for user testimonials.
export const LANDING_TESTIMONIALS_TH_USER_IMAGES = {
  user1: `${LANDING_ASSET_BASE_PATH}/images/reviewers/th-testimonial-user-1.webp`,
  user2: `${LANDING_ASSET_BASE_PATH}/images/reviewers/th-testimonial-user-2.webp`,
  user3: `${LANDING_ASSET_BASE_PATH}/images/reviewers/th-testimonial-user-3.webp`,
  user4: `${LANDING_ASSET_BASE_PATH}/images/reviewers/th-testimonial-user-4.webp`,
  user5: `${LANDING_ASSET_BASE_PATH}/images/reviewers/th-testimonial-user-5.webp`,
} as const;

// Standard testimonial item (PH, ID, ZA) with rating stars.
export interface LandingTestimonialItem {
  id: string;
  imagePath: string;
  rating: number;
  nameKey: string;
  testimonyKey: string;
}

// Thailand user testimonial item (no rating, uses quote icon).
export interface LandingUserTestimonialItem {
  id: string;
  avatarPath: string;
  contentKey: string;
  nameKey: string;
}

// Countries that display standard testimonials (with rating stars).
export const LANDING_STANDARD_TESTIMONIALS_COUNTRIES: CountryCodeEnum[] = [
  CountryCodeEnum.ph,
  CountryCodeEnum.id,
  CountryCodeEnum.za,
];

// Standard testimonial items (shared structure, translations differ by country).
export const LANDING_STANDARD_TESTIMONIAL_ITEMS: LandingTestimonialItem[] = [
  {
    id: "reviewer1",
    imagePath: LANDING_TESTIMONIALS_REVIEWER_IMAGES.reviewer1,
    rating: 4.5,
    nameKey: "LANDING.TESTIMONIALS.ITEMS.REVIEWER_1.NAME",
    testimonyKey: "LANDING.TESTIMONIALS.ITEMS.REVIEWER_1.TESTIMONY",
  },
  {
    id: "reviewer2",
    imagePath: LANDING_TESTIMONIALS_REVIEWER_IMAGES.reviewer2,
    rating: 4,
    nameKey: "LANDING.TESTIMONIALS.ITEMS.REVIEWER_2.NAME",
    testimonyKey: "LANDING.TESTIMONIALS.ITEMS.REVIEWER_2.TESTIMONY",
  },
  {
    id: "reviewer3",
    imagePath: LANDING_TESTIMONIALS_REVIEWER_IMAGES.reviewer3,
    rating: 5,
    nameKey: "LANDING.TESTIMONIALS.ITEMS.REVIEWER_3.NAME",
    testimonyKey: "LANDING.TESTIMONIALS.ITEMS.REVIEWER_3.TESTIMONY",
  },
  {
    id: "reviewer4",
    imagePath: LANDING_TESTIMONIALS_REVIEWER_IMAGES.reviewer4,
    rating: 5,
    nameKey: "LANDING.TESTIMONIALS.ITEMS.REVIEWER_4.NAME",
    testimonyKey: "LANDING.TESTIMONIALS.ITEMS.REVIEWER_4.TESTIMONY",
  },
];

// Thailand user testimonial items.
export const LANDING_TH_USER_TESTIMONIAL_ITEMS: LandingUserTestimonialItem[] = [
  {
    id: "reviewer1",
    avatarPath: LANDING_TESTIMONIALS_TH_USER_IMAGES.user1,
    contentKey: "LANDING.TESTIMONIALS.TH_USERS.REVIEWER_1.CONTENT",
    nameKey: "LANDING.TESTIMONIALS.TH_USERS.REVIEWER_1.NAME",
  },
  {
    id: "reviewer2",
    avatarPath: LANDING_TESTIMONIALS_TH_USER_IMAGES.user2,
    contentKey: "LANDING.TESTIMONIALS.TH_USERS.REVIEWER_2.CONTENT",
    nameKey: "LANDING.TESTIMONIALS.TH_USERS.REVIEWER_2.NAME",
  },
  {
    id: "reviewer3",
    avatarPath: LANDING_TESTIMONIALS_TH_USER_IMAGES.user3,
    contentKey: "LANDING.TESTIMONIALS.TH_USERS.REVIEWER_3.CONTENT",
    nameKey: "LANDING.TESTIMONIALS.TH_USERS.REVIEWER_3.NAME",
  },
  {
    id: "reviewer4",
    avatarPath: LANDING_TESTIMONIALS_TH_USER_IMAGES.user4,
    contentKey: "LANDING.TESTIMONIALS.TH_USERS.REVIEWER_4.CONTENT",
    nameKey: "LANDING.TESTIMONIALS.TH_USERS.REVIEWER_4.NAME",
  },
  {
    id: "reviewer5",
    avatarPath: LANDING_TESTIMONIALS_TH_USER_IMAGES.user5,
    contentKey: "LANDING.TESTIMONIALS.TH_USERS.REVIEWER_5.CONTENT",
    nameKey: "LANDING.TESTIMONIALS.TH_USERS.REVIEWER_5.NAME",
  },
];

// Swiper configuration for testimonials sliders.
export const LANDING_TESTIMONIALS_SWIPER_CONFIG = {
  slidesPerView: 3, // Default slides visible on desktop
  spaceBetween: 16, // Gap between slides in pixels
  autoplayDelay: 5000, // Auto-advance delay in milliseconds
  loop: true, // Enable infinite loop
  centeredSlides: true, // Center the active slide
  breakpoints: {
    0: { slidesPerView: 1, centeredSlides: true }, // Mobile: 1 slide
    576: { slidesPerView: 2, centeredSlides: true }, // Tablet: 2 slides
    992: { slidesPerView: 3, centeredSlides: true }, // Desktop: 3 slides
  },
};
